import { defineStore } from 'pinia';
import { useStudyStore } from './study';
import { reactive, ref } from 'vue';
import StudyGroupService from '@/services/studyGroupService';
import { StudyGroupModel, type IGroupInviteLink, type IStudyGroupUser } from '@/models/groups';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import { LoadingStateModel } from '@/models/loadingState';
import InviteService from '@/services/inviteService';
import StudyUsersService from '@/services/studyUsersService';
import type { UserModel } from '@/models/user';
import { useFormStore } from './forms';

export const useStudyGroupsStore = defineStore('studyGroupStore', () => {

  const tableColumns: TableColumnModel[] = [
    {
      id: 1,
      header: 'Name',
      key: 'name',
      type: TableColumnType.TEXT,
      sortable: true
    },
    {
      id: 2,
      header: 'Members',
      type: TableColumnType.TEXT,
      key: 'members',
      sortable: false
    },
    {
      id: 3,
      header: 'Creation Date',
      type: TableColumnType.TEXT,
      key: 'createdAt',
      sortable: true
    },
    {
      id: 4,
      header: 'Last edited on',
      type: TableColumnType.TEXT,
      key: 'updatedAt',
      sortable: true
    }
  ];

  const studyGroups = ref<StudyGroupModel[]>([]);
  const studyStore = useStudyStore();
  const formStore = useFormStore();
  const selectedGroup = ref<StudyGroupModel>(
    StudyGroupModel.createEmpty(formStore.formList)
  );
  const group = ref<StudyGroupModel>();
  const updatedGroup = ref<StudyGroupModel>();
  const groupLinkActivated = ref<boolean>(false);
  const viewEntriesPermissions = ['View all entries', 'View own entries'];
  const selectedViewEntryPermission = ref(viewEntriesPermissions[0]);

  const updateGroup = (incomingGroup: StudyGroupModel) => {
    const newGroup: StudyGroupModel = JSON.parse(JSON.stringify(incomingGroup));


    if (typeof newGroup.permissions.forms === 'boolean') {
      newGroup.permissions.forms = formStore.formList.map(
        form => form.base64Id
      ) as string[];
      newGroup.permissions.editForms = formStore.formList.map(
        form => form.base64Id
      ) as string[];
    }

    selectedGroup.value = JSON.parse(JSON.stringify(newGroup));

    group.value = JSON.parse(JSON.stringify(newGroup));
    updatedGroup.value = JSON.parse(JSON.stringify(newGroup));

    if (newGroup.create) {
      groupLinkActivated.value = false;
      selectedViewEntryPermission.value = viewEntriesPermissions[0];
    } else {
      groupLinkActivated.value =
        newGroup.inviteLink !== undefined &&
        newGroup.inviteLink !== null &&
        (newGroup.inviteLink as IGroupInviteLink)?.disabled !== true;
      selectedViewEntryPermission.value = newGroup.permissions
        .viewEntries ?
        viewEntriesPermissions[0] :
        viewEntriesPermissions[1];
    }
  };

  const setSelectedGroup = (groupId: string) => {
    const groupUpdated = studyGroups.value.find(groupItem => groupItem.identifier === groupId);
    if (groupUpdated) {
      updateGroup(groupUpdated);
    }
  };

  const isStudyGroupsLoading = reactive(new LoadingStateModel({ isLoading: false }));
  const getStudyGroups = async () => {
    if (studyStore.selectedStudyId) {
      try {
        isStudyGroupsLoading.startLoading();
        studyGroups.value = await StudyGroupService.getStudyGroups(studyStore.selectedStudyId, formStore.formList);
      } catch (error) {
        isStudyGroupsLoading.setError(error);
      } finally {
        isStudyGroupsLoading.stopLoadingAndFinish();
      }
    }
  };


  const isStudyGroupUpdating = reactive(new LoadingStateModel({ isLoading: false }));
  const updateStudyGroup = async (incomingGroup: StudyGroupModel) => {
    try {
      isStudyGroupUpdating.startLoading();
      await StudyGroupService.patchStudyGroup(incomingGroup);
      isStudyGroupUpdating.setSuccess(`Group ${incomingGroup.name} is updated!`);
    } catch (error) {
      isStudyGroupUpdating.setError(error);
    } finally {
      isStudyGroupUpdating.stopLoadingAndFinish();
    }
  };


  const isActivatingGroupLink = reactive(new LoadingStateModel({ isLoading: false }));
  const activateGroupLink = async (groupId: string) => {
    try {
      isActivatingGroupLink.startLoading();
      await StudyGroupService.activateGroupLink(groupId);
      isActivatingGroupLink.setSuccess(`Sharable group link activated.`);
      await getStudyGroups();
      setSelectedGroup(groupId);
    } catch (error) {
      isActivatingGroupLink.setError(error);
    } finally {
      isActivatingGroupLink.stopLoadingAndFinish();
    }
  };


  const isDeactivatingGroupLink = reactive(new LoadingStateModel({ isLoading: false }));
  const deactivateGroupLink = async (groupId: string) => {
    try {
      isDeactivatingGroupLink.startLoading();
      await StudyGroupService.deactivateGroupLink(groupId);
      isDeactivatingGroupLink.setSuccess(`Sharable group link diactivated!`);
      await getStudyGroups();
      setSelectedGroup(groupId);
    } catch (error) {
      isDeactivatingGroupLink.setError(error);
    } finally {
      isDeactivatingGroupLink.stopLoadingAndFinish();
    }
  };


  const isResettingGroupLink = reactive(new LoadingStateModel({ isLoading: false }));
  const resetGroupLink = async (groupId: string) => {
    try {
      isResettingGroupLink.startLoading();
      await StudyGroupService.resetGroupLink(groupId);
      isResettingGroupLink.setSuccess(`Sharable group link did reset.`);
      await getStudyGroups();
      setSelectedGroup(groupId);
    } catch (error) {
      isResettingGroupLink.setError(error);
    } finally {
      isResettingGroupLink.stopLoadingAndFinish();
    }
  };


  const isReInvitingUser = reactive(new LoadingStateModel({ isLoading: false }));
  const reInviteUserToStudy = async (invite: IStudyGroupUser) => {
    try {
      isReInvitingUser.startLoading();
      await InviteService.resendUserInvite(invite.identifier);
      isReInvitingUser.setSuccess(`Invite sent!`);
    } catch (error) {
      isReInvitingUser.setError(error);
    } finally {
      isReInvitingUser.stopLoadingAndFinish();
    }
  };


  const isDeletingInvitedUser = reactive(new LoadingStateModel({ isLoading: false }));
  const deleteInvitedUserFromGroup = async (groupId: string, invite: IStudyGroupUser) => {
    try {
      isDeletingInvitedUser.startLoading();
      await InviteService.deleteUserInvite(invite.identifier);
      isDeletingInvitedUser.setSuccess(`Invite deleted!`);
      await getStudyGroups();
      setSelectedGroup(groupId);
    } catch (error) {
      isDeletingInvitedUser.setError(error);
    } finally {
      isDeletingInvitedUser.stopLoadingAndFinish();
    }
  };


  const isRemovingGroupUser = reactive(new LoadingStateModel({ isLoading: false }));
  const removeUserFromGroup = async (groupId: string, invite: IStudyGroupUser) => {
    try {
      isRemovingGroupUser.startLoading();
      await StudyGroupService.removeUserFromGroup(groupId, invite.identifier);
      isRemovingGroupUser.setSuccess(`User removed from the study.`);
      await getStudyGroups();
      setSelectedGroup(groupId);
    } catch (error) {
      isRemovingGroupUser.setError(error);
    } finally {
      isRemovingGroupUser.stopLoadingAndFinish();
    }
  };


  const isInvitingUser = reactive(new LoadingStateModel({ isLoading: false }));
  const inviteUserToGroup = async (invites: UserModel[], groupId: string) => {
    if (studyStore.selectedStudyId) {
      try {
        isInvitingUser.startLoading();
        await StudyUsersService.inviteUserToStudy(studyStore.selectedStudyId, invites, groupId);
        if (invites.length > 1) {
          isInvitingUser.setSuccess(`${invites.length} users invited to study!`);
        } else {
          isInvitingUser.setSuccess(`${invites[0].email} was invited to study!`);
        }
        await getStudyGroups();
        setSelectedGroup(groupId);
      } catch (error) {
        isInvitingUser.setError(error);
      } finally {
        isInvitingUser.stopLoadingAndFinish();
      }
    }
  };


  const isDeletingGroup = reactive(new LoadingStateModel({ isLoading: false }));
  const deleteGroup = async (incomingGroup: StudyGroupModel) => {
    try {
      isDeletingGroup.startLoading();
      await StudyGroupService.deleteStudyGroup(incomingGroup);
      isDeletingGroup.setSuccess(`Group "${incomingGroup.name}" is deleted!`);
      await getStudyGroups();
    } catch (error) {
      isDeletingGroup.setError(error);
    } finally {
      isDeletingGroup.stopLoadingAndFinish();
    }
  };


  const isStudyGroupCreating = reactive(new LoadingStateModel({ isLoading: false }));
  const createStudyGroup = async (incomingGroup: StudyGroupModel) => {
    if (studyStore.selectedStudyId) {
      try {
        isStudyGroupCreating.startLoading();
        await StudyGroupService.postStudyGroup(studyStore.selectedStudyId, incomingGroup);
        isStudyGroupCreating.setSuccess(`Group ${incomingGroup.name} is created!`);
      } catch (error) {
        isStudyGroupCreating.setError(error);
      } finally {
        isStudyGroupCreating.stopLoadingAndFinish();
      }
    }
  };

  return {
    getStudyGroups,
    updateStudyGroup,
    activateGroupLink,
    deactivateGroupLink,
    resetGroupLink,
    reInviteUserToStudy,
    deleteInvitedUserFromGroup,
    removeUserFromGroup,
    inviteUserToGroup,
    deleteGroup,
    createStudyGroup,
    updateGroup,
    tableColumns,
    studyGroups,
    selectedGroup,
    group,
    updatedGroup,
    groupLinkActivated,
    viewEntriesPermissions,
    selectedViewEntryPermission,
    isStudyGroupsLoading,
    isStudyGroupUpdating,
    isActivatingGroupLink,
    isDeactivatingGroupLink,
    isResettingGroupLink,
    isReInvitingUser,
    isDeletingInvitedUser,
    isRemovingGroupUser,
    isInvitingUser,
    isDeletingGroup,
    isStudyGroupCreating
  };
});
